import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { NerSearchQuery } from 'services/internalSearch/types';
import { DealerPriceAvailabilityResponse } from 'services/dealerPriceAvailability/types';
import {
  transformPriceAndAvailability,
  transformPriceAndAvailabilityHeadless
} from 'services/dealerPriceAvailability/utils';
import { PCC_SEARCH_RESULTS } from '../constants/analytics-constants';
import { isFilterByEquipment } from 'app/[lang]/[esite]/utils/filter-by-equipment';
import { PriceData } from '../_components/analytics-listener/types';
import { PARTS_DIAGRAM_PATH } from 'constants/links';
import { getSeoUrl } from 'utils/links';
import { PriceAvailabilityResponseHeadless } from 'services/priceAvailability/types';
import { decodeString } from '@cat-ecom/common/utils/stringUtils';
import { PageParamType } from '../types';
import {
  CATEGORY_ID_PARAM,
  FACET_PARAM
} from 'hooks/use-filter/constants/common';

export const hasProducts = (productCount: number) => productCount !== 0;

export const isNerSearch = (
  nerSearchQuery: NerSearchQuery,
  isNerEnabled?: string
) => {
  const { modifiedQuery } = nerSearchQuery || {};
  return (
    (!isEmpty(nerSearchQuery) && !isEmpty(modifiedQuery)) ||
    /true/.test(isNerEnabled)
  );
};

const getUnformattedTotalPrice = (
  priceData: DealerPriceAvailabilityResponse,
  partNumber: string
) => {
  const price = priceData.priceAndAvailability.find(
    (priceData) => priceData.partNumber === partNumber
  );

  return price?.unformattedTotalPrice;
};

const processFluidAttributes = (fluidAttributes?: string[]) => {
  if (!Array.isArray(fluidAttributes)) return null;
  return fluidAttributes.reduce((acc, item) => {
    const [dealerCode, fluidPartNumber, sosCode] = item.split(',');
    if (dealerCode && fluidPartNumber && sosCode) {
      return {
        ...acc,
        [dealerCode]: {
          fluidPartNumber,
          sosCode
        }
      };
    }
    return acc;
  }, {});
};
export const getFluidAttributeData = (productPriceData, dealerCode: string) => {
  if (productPriceData?.fluidAttributes && dealerCode) {
    const processedFluidAttributes = processFluidAttributes(
      productPriceData.fluidAttributes
    );
    if (processedFluidAttributes[dealerCode]) {
      return processedFluidAttributes[dealerCode];
    }
  }
  return {};
};

export const transformProductPriceData = (
  products: Product[],
  priceData:
    | DealerPriceAvailabilityResponse
    | PriceAvailabilityResponseHeadless,
  serialNumber: string,
  fitmentList: ProductFitmentResponse,
  listNameAndId: string = PCC_SEARCH_RESULTS,
  isPriceAndAvailabilityV1?: boolean
) => {
  let productPriceData: PriceData;
  const data = products?.map((product) => {
    if (priceData) {
      const transformedPriceData = isPriceAndAvailabilityV1
        ? transformPriceAndAvailabilityHeadless(
            priceData as PriceAvailabilityResponseHeadless,
            product.partNumber
          )
        : transformPriceAndAvailability(
            priceData as DealerPriceAvailabilityResponse,
            product.partNumber
          );

      const {
        item: {
          availability,
          price,
          weight,
          quantity,
          categoryPath,
          replacementParts,
          partLevelFlags,
          sosCode
        },
        currency,
        isGstAvailable,
        isGstIncludesTax
      } = transformedPriceData;

      const unformattedTotalPrice = isPriceAndAvailabilityV1
        ? '' + price.unformattedTotalPrice
        : getUnformattedTotalPrice(
            priceData as DealerPriceAvailabilityResponse,
            product.partNumber
          );
      productPriceData = {
        availability,
        price,
        weight,
        quantity,
        categoryPath,
        currency,
        unformattedTotalPrice,
        replacementParts,
        isGstAvailable,
        isGstIncludesTax,
        partLevelFlags,
        sosCode
      };
    }
    const fitmentCheck = fitmentList?.[product.partNumber]?.isSerialCompatible
      ? 'fity'
      : 'fitn';

    return {
      ...product,
      ...(priceData && { productPriceData }),
      serialNumber,
      fitmentCheck,
      listNameAndId
    };
  });
  return data;
};

export const getUpdatedProductCount = (productCount: number) => {
  const exceedingResults = 1200;
  return productCount > 1200 ? exceedingResults : productCount;
};

export const isNerActive = (
  isNerEnabled: string,
  filterByEquipment: string,
  isMatchingMyEquipment: boolean,
  shouldDisplayLoggedInNerModal = false
) =>
  isNerEnabled !== 'false' &&
  (isFilterByEquipment(filterByEquipment) || shouldDisplayLoggedInNerModal) &&
  !isMatchingMyEquipment;

export const getPartsDiagramUrl = (
  esiteParams: esiteParams | Record<string, string | string[]>,
  systemId: string,
  componentId: string,
  systemControlNumber: string
) => {
  const esiteUrl = getSeoUrl(esiteParams);
  return `${esiteUrl}${PARTS_DIAGRAM_PATH}?systemId=${systemId}&componentId=${componentId}&ieSystemControlNumber=${systemControlNumber}&back=true`;
};

export const normalizeSearchParamsFacets = (
  value: string | string[] | undefined
) => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export const getFacetParams = (facetsArray: string[]) => {
  const facets = facetsArray?.length === 1 ? facetsArray[0] : facetsArray;
  return facetsArray?.length === 0 ? undefined : facets;
};

type GetSearchCategoriesProps = {
  categories: { name: string; id: string }[];
  params: URLSearchParams;
  pathname: string;
  pageParam?: string;
};
export const getSearchCategories = ({
  categories,
  params,
  pathname,
  pageParam
}: GetSearchCategoriesProps) => {
  return categories.map(({ id, ...rest }) => {
    const decodedId = decodeString(id);
    const page = pageParam ?? PageParamType.PAGE;
    const newParams = new URLSearchParams(params.toString());
    newParams.set(CATEGORY_ID_PARAM, decodedId);
    newParams.set(page, '1');
    newParams.delete(FACET_PARAM);
    const url = `${pathname}?${newParams.toString()}`;
    return {
      id,
      url,
      ...rest
    };
  });
};
